export type OptionalGroup = {
  name: string;
  /** Icon key from GROUP_ICON_KEYS; omit for no / default icon. */
  icon?: string;
};

export const OPTIONAL_GROUPS_MAX = 30;

const STORAGE_PREFIX = 'lf_optional_room_groups_';

export const storageKeyForOptionalGroups = (userKey: string, hotelId?: string | number | null) => (
  `${STORAGE_PREFIX}${hotelId != null && hotelId !== '' ? String(hotelId) : '0'}_${userKey || 'anon'}`
);

export const loadOptionalGroups = (
  userKey: string,
  hotelId?: string | number | null,
): OptionalGroup[] => {
  try {
    const raw = localStorage.getItem(storageKeyForOptionalGroups(userKey, hotelId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((g): g is OptionalGroup => g && typeof g.name === 'string' && g.name.trim().length > 0)
      .map((g) => ({
        name: String(g.name).trim(),
        ...(g.icon ? { icon: String(g.icon) } : {}),
      }))
      .slice(0, OPTIONAL_GROUPS_MAX);
  } catch {
    return [];
  }
};

export const saveOptionalGroups = (
  userKey: string,
  hotelId: string | number | null | undefined,
  groups: OptionalGroup[],
) => {
  localStorage.setItem(
    storageKeyForOptionalGroups(userKey, hotelId),
    JSON.stringify(groups.slice(0, OPTIONAL_GROUPS_MAX)),
  );
};

export const addOptionalGroup = (
  userKey: string,
  hotelId: string | number | null | undefined,
  group: OptionalGroup,
  reservedNames: string[] = [],
): { ok: true; groups: OptionalGroup[] } | { ok: false; error: string } => {
  const name = group.name.trim();
  if (!name) return { ok: false, error: 'Group name is required' };
  if (name.length > 25) return { ok: false, error: '25 characters or less allowed' };
  const existing = loadOptionalGroups(userKey, hotelId);
  if (existing.length >= OPTIONAL_GROUPS_MAX) {
    return { ok: false, error: `You can only save ${OPTIONAL_GROUPS_MAX} optional groups` };
  }
  const lower = name.toLowerCase();
  if (existing.some((g) => g.name.toLowerCase() === lower)) {
    return { ok: false, error: 'That optional group already exists' };
  }
  if (reservedNames.some((n) => n.toLowerCase() === lower)) {
    return { ok: false, error: 'That name is already a default group' };
  }
  const next = [...existing, { name, ...(group.icon ? { icon: group.icon } : {}) }];
  saveOptionalGroups(userKey, hotelId, next);
  return { ok: true, groups: next };
};

export const deleteOptionalGroup = (
  userKey: string,
  hotelId: string | number | null | undefined,
  name: string,
): OptionalGroup[] => {
  const next = loadOptionalGroups(userKey, hotelId)
    .filter((g) => g.name.toLowerCase() !== name.trim().toLowerCase());
  saveOptionalGroups(userKey, hotelId, next);
  return next;
};
